'use client';

import { COUNTRIES, COUNTRIES_WITH_PROVINCES, COUNTRIES_WITH_STATES, getAutoCompleteCity, isPoBox, STATES, US_TERRITORY_COUNTRY_CODES } from '@onefootprint/core';
import type { Iso3166TwoDigitCountryCode, Locale } from '@onefootprint/request-types';
import { useEffect, useId } from 'react';
import type { FieldValues, Path, PathValue } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import AddressInput from '../../../address-input';
import FormWrapper from '../../internals/form-wrapper';
import FormCountry, { type CountrySelectOption } from '../form-country';
import FormInput from '../form-input/form-input';
import FormSelect from '../form-select';
import FormZip from '../form-zip';

// `getDetails` rejects with a bare Google `PlacesServiceStatus` string (NOT_FOUND,
// DEADLINE_EXCEEDED, ...) when Google can't resolve the place. Only an `Error` is ours.
const reportAddressParseError = (error: unknown) => {
  if (error instanceof Error) {
    console.error('Error parsing address:', error);
  }
};
type Orientation = 'vertical' | 'horizontal' | 'horizontal-justify-between';
export type FormAddressLinesProps = {
  allowPoBox?: boolean;
  alwaysShowState?: boolean;
  disableCountry?: boolean;
  /** Per-field read-only flags, for when only some of the address is already known */
  disabled?: {
    country?: boolean;
    addressLine1?: boolean;
    addressLine2?: boolean;
    city?: boolean;
    state?: boolean;
    zip?: boolean;
  };
  /** CSS class applied to each field component for width/layout control */
  fieldClassName?: string;
  orientation?: Orientation;
  prefix?: string;
  size?: 'compact' | 'default';
  /** Separator between prefix and field name for vault data (e.g., `__DOT__` for `id.country` → `id__DOT__country`). Defaults to '_' when prefix is provided. */
  vaultSeparator?: string;
  required?: boolean;
  l10n?: {
    locale?: Locale;
  };
  countries?: CountrySelectOption[];
  /** Options for the US state dropdown. Defaults to the 50 states plus DC. */
  stateOptions?: {
    label: string;
    value: string;
  }[];
  labels?: {
    country?: string;
    addressLine1?: string;
    addressLine1International?: string;
    addressLine2?: string;
    addressLine2International?: string;
    city?: string;
    state?: string;
    stateInternational?: string;
    zip?: string;
  };
  placeholders?: {
    country?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLine2International?: string;
    city?: string;
    state?: string;
    stateInternational?: string;
    zip?: string;
  };
  errors?: {
    country?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  hints?: {
    countryDisabled?: string;
  };
};
export default function FormAddressLines<TFieldValues extends FieldValues>({
  allowPoBox,
  alwaysShowState,
  disableCountry,
  disabled = {},
  fieldClassName,
  orientation = 'vertical',
  prefix = '',
  size = 'default',
  vaultSeparator,
  required = false,
  l10n,
  countries = COUNTRIES,
  stateOptions = STATES,
  labels = {},
  placeholders = {},
  errors: errorMessages = {},
  hints = {}
}: FormAddressLinesProps) {
  const {
    register,
    formState: {
      errors
    },
    setValue,
    resetField,
    setFocus,
    control
  } = useFormContext<TFieldValues>();
  const addressLine1Id = useId();

  // Build field names with prefix
  const separator = vaultSeparator ?? (prefix ? '_' : '');
  const countryName = `${prefix}${separator}country` as Path<TFieldValues>;
  const addressLine1Name = `${prefix}${separator}address_line1` as Path<TFieldValues>;
  const addressLine2Name = `${prefix}${separator}address_line2` as Path<TFieldValues>;
  const cityName = `${prefix}${separator}city` as Path<TFieldValues>;
  const stateName = `${prefix}${separator}state` as Path<TFieldValues>;
  const zipName = `${prefix}${separator}zip` as Path<TFieldValues>;
  const country = useWatch({
    control,
    name: countryName
  });
  const isCountryUs = country === 'US';
  const isCountryUsOrTerritory = isCountryUs || US_TERRITORY_COUNTRY_CODES.includes(country as string);
  const shouldDisableCountry = disableCountry || countries.length === 1;
  const shouldCollectState = alwaysShowState || COUNTRIES_WITH_STATES.includes(country as Iso3166TwoDigitCountryCode) || COUNTRIES_WITH_PROVINCES.includes(country as Iso3166TwoDigitCountryCode);

  // Handle country change - reset all address fields
  const handleCountryChange = () => {
    resetField(addressLine1Name);
    resetField(addressLine2Name);
    resetField(cityName);
    resetField(stateName);
    setValue(stateName, '' as PathValue<TFieldValues, Path<TFieldValues>>);
    resetField(zipName);
    setFocus(addressLine1Name);
  };
  const handleAddressSelect = async (prediction?: google.maps.places.AutocompletePrediction | null) => {
    if (!prediction) {
      return;
    }
    resetField(addressLine1Name);
    resetField(addressLine2Name);
    resetField(cityName);
    resetField(stateName);
    resetField(zipName);
    const formattedStreetAddress = prediction?.structured_formatting.main_text;
    if (formattedStreetAddress) {
      setValue(addressLine1Name, formattedStreetAddress as PathValue<TFieldValues, Path<TFieldValues>>);
    }

    // Parse address from Google Places - simplified version without external dependency
    // This is a basic implementation that consumers can enhance if needed
    if (prediction.place_id) {
      try {
        const {
          getDetails
        } = await import('use-places-autocomplete');
        const result = await getDetails({
          placeId: prediction.place_id
        });
        if (typeof result === 'object' && result?.address_components) {
          const addressComponents = result.address_components;

          // Helper to get component
          const getComponent = (type: string) => addressComponents.find(c => c.types.includes(type))?.long_name;
          const city = getAutoCompleteCity(addressComponents, prediction.structured_formatting.secondary_text);
          const state = getComponent('administrative_area_level_1');
          const zip = getComponent('postal_code');
          if (city) {
            setValue(cityName, city as PathValue<TFieldValues, Path<TFieldValues>>);
          }
          if (state) {
            if (isCountryUs) {
              const possibleState = stateOptions.find(stateOption => stateOption.label === state);
              if (possibleState) {
                setValue(stateName, possibleState.value as PathValue<TFieldValues, Path<TFieldValues>>);
              }
            } else {
              setValue(stateName, state as PathValue<TFieldValues, Path<TFieldValues>>);
            }
          }
          if (zip) {
            setValue(zipName, zip as PathValue<TFieldValues, Path<TFieldValues>>);
          }
        }
      } catch (error) {
        reportAddressParseError(error);
      }
    }
  };
  const getError = (fieldName: Path<TFieldValues>) => {
    const fieldError = errors[fieldName];
    return fieldError ? String(fieldError.message || '') : undefined;
  };
  const singleCountryValue = shouldDisableCountry && countries.length > 0 ? countries[0].value : undefined;

  // When there's only one selectable country, sync the form value after the Controller
  // has fully initialized. setTimeout(0) is needed because RHF's internal state
  // reconciliation overwrites synchronous setValue calls during the same event loop tick.
  // Using `country` (from useWatch) instead of `getValues` makes this reactive — if an
  // external agent (e.g. LastPass, browser autofill) clears the country value after
  // initial mount, this effect will re-fire and restore it.
  useEffect(() => {
    if (singleCountryValue && country !== singleCountryValue) {
      setTimeout(() => {
        setValue(countryName, singleCountryValue as PathValue<TFieldValues, Path<TFieldValues>>, {
          shouldValidate: true,
          shouldDirty: true
        });
      }, 0);
    }
  }, [singleCountryValue, country, countryName, setValue]);
  const isHorizontal = orientation === 'horizontal' || orientation === 'horizontal-justify-between';
  const addressLine1Label = isCountryUs ? labels.addressLine1 : labels.addressLine1International || labels.addressLine1;
  const addressLine2Label = isCountryUs ? labels.addressLine2 : labels.addressLine2International || labels.addressLine2;
  const addressLine2Placeholder = isCountryUs ? placeholders.addressLine2 : placeholders.addressLine2International || placeholders.addressLine2;
  const addressLine1Registration = register(addressLine1Name, {
    required: required ? errorMessages.addressLine1 : false,
    maxLength: {
      value: 1000,
      message: errorMessages.addressLine1 ?? ''
    },
    validate: value => {
      if (!value) return true;
      if (!allowPoBox && isPoBox(value) && errorMessages.addressLine1) return errorMessages.addressLine1;
      return true;
    }
  });
  const renderAddressLine1 = () => {
    if (isHorizontal) {
      return <FormWrapper id={addressLine1Id} label={addressLine1Label} orientation={orientation} size={size} fullWidth error={getError(addressLine1Name)} errorPosition="relative">
          <AddressInput id={addressLine1Id} data-attribute="address-line-1" data-dd-privacy="mask" country={country as Iso3166TwoDigitCountryCode} disabled={disabled.addressLine1} hasError={!!errors[addressLine1Name]} size={size} className={fieldClassName} containerClassName="w-full" onSelect={handleAddressSelect} placeholder={placeholders.addressLine1} {...addressLine1Registration} />
        </FormWrapper>;
    }
    return <AddressInput data-attribute="address-line-1" data-dd-privacy="mask" country={country as Iso3166TwoDigitCountryCode} disabled={disabled.addressLine1} hasError={!!errors[addressLine1Name]} hint={getError(addressLine1Name)} size={size} label={addressLine1Label} onSelect={handleAddressSelect} placeholder={placeholders.addressLine1} className={fieldClassName} {...addressLine1Registration} />;
  };
  const renderCityAndZip = () => {
    if (isHorizontal) {
      return <>
          <FormInput<TFieldValues> autoComplete="address-level2" data-nid-target="city" data-dd-privacy="mask" disabled={disabled.city} size={size} className={fieldClassName} orientation={orientation} label={labels.city} placeholder={placeholders.city} name={cityName} rules={{
          required: required ? errorMessages.city : false,
          maxLength: {
            value: 1000,
            message: errorMessages.city ?? ''
          }
        }} />
          <FormZip<TFieldValues> name={zipName} country={country as Iso3166TwoDigitCountryCode} disabled={disabled.zip} size={size} className={fieldClassName} orientation={orientation} label={labels.zip} placeholder={placeholders.zip} data-nid-target="zip" data-dd-privacy="mask" rules={{
          required: required && isCountryUsOrTerritory ? errorMessages.zip : false
        }} />
        </>;
    }
    return <div className="flex gap-4">
        <div className="flex-1">
          <FormInput<TFieldValues> autoComplete="address-level2" data-nid-target="city" data-dd-privacy="mask" disabled={disabled.city} size={size} label={labels.city} placeholder={placeholders.city} name={cityName} rules={{
          required: required ? errorMessages.city : false,
          maxLength: {
            value: 1000,
            message: errorMessages.city ?? ''
          }
        }} />
        </div>
        <div className="flex-1">
          <FormZip<TFieldValues> name={zipName} country={country as Iso3166TwoDigitCountryCode} disabled={disabled.zip} size={size} label={labels.zip} placeholder={placeholders.zip} data-nid-target="zip" data-dd-privacy="mask" rules={{
          required: required && isCountryUsOrTerritory ? errorMessages.zip : false
        }} />
        </div>
      </div>;
  };
  return <div className="flex flex-col gap-4">
      <FormCountry<TFieldValues>
    // @ts-expect-error - Dynamic prefix creates type complexity
    name={countryName} size={size} className={fieldClassName} orientation={orientation} label={labels.country} placeholder={placeholders.country} locale={l10n?.locale} onChange={handleCountryChange} disabled={disabled.country || shouldDisableCountry} hint={shouldDisableCountry ? hints.countryDisabled : undefined} options={countries} rules={{
      required: required ? errorMessages.country : false
    }} value={singleCountryValue} />

      {renderAddressLine1()}

      <FormInput<TFieldValues> name={addressLine2Name} data-dd-privacy="mask" data-attribute="address-line-2" autoComplete="address-line2" disabled={disabled.addressLine2} size={size} className={fieldClassName} orientation={orientation} label={addressLine2Label} placeholder={addressLine2Placeholder} rules={{
      setValueAs: (value_0: string) => !value_0 || value_0 === '' ? undefined : value_0,
      validate: value_1 => !allowPoBox && isPoBox(value_1) ? errorMessages.addressLine2 : true
    }} />

      {renderCityAndZip()}

      {shouldCollectState && isCountryUs && <FormSelect<TFieldValues> data-nid-target="state"
    // @ts-expect-error - Dynamic prefix creates type complexity
    name={stateName} disabled={disabled.state} size={size} className={fieldClassName} orientation={orientation} label={labels.state} options={stateOptions} placeholder={placeholders.state} data-dd-privacy="mask" rules={{
      required: required ? errorMessages.state : false
    }} />}
      {shouldCollectState && !isCountryUs && <FormInput<TFieldValues> name={stateName} autoComplete="address-level1" data-nid-target="state" data-dd-privacy="mask" disabled={disabled.state} size={size} className={fieldClassName} orientation={orientation} label={labels.stateInternational || labels.state} placeholder={placeholders.stateInternational || placeholders.state} />}
    </div>;
}